import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import images from "../../images";
import { toast } from "react-toastify";
import { FormField } from "../../components/common/Input/Input";
import { usePostSearchCompanyMutation } from "../../services/apiSlice";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import { Helmet } from "react-helmet-async";
import Copyright from "../../layout/Copyright/Copyright";
import { useTranslation } from "react-i18next";

export default function SearchCompanyPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [buttonText, setButtonText] = useState(t("base.next"));
  const [errorMessage, setErrorMessage] = useState("");
  const [postSearchCompany, { isLoading: isSearchCompany }] =
    usePostSearchCompanyMutation();

  // Memoize urlRegex to prevent recreation on every render
  const urlRegex = useMemo(
    () => /^https:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/.*)?$/,
    []
  );

  const handleInputChange = (e) => {
    const { value } = e.target;
    setSearchQuery(value);
  };

  // Update button text and error message based on searchQuery validity
  useEffect(() => {
    if (!searchQuery.trim()) {
      setButtonText(t("base.next"));
      setErrorMessage("");
    } else if (urlRegex.test(searchQuery.trim())) {
      setButtonText(t("base.search"));
      setErrorMessage("");
    } else {
      setButtonText(t("base.next"));
      setErrorMessage(t("base.invalidUrl"));
    }
  }, [searchQuery, t, urlRegex]);

  const handleSearch = async () => {
    // If searchQuery is empty or invalid, navigate to /profile-company
    if (!searchQuery.trim() || !urlRegex.test(searchQuery.trim())) {
      navigate("/profile-company", { state: { companyData: null } });
      return;
    }

    // If searchQuery is a valid URL, call API
    const body = {
      url: searchQuery,
    };
    try {
      const responSearchCompany = await postSearchCompany(body).unwrap();
      if (responSearchCompany.description) {
        toast.success(t("toast.success.getInformationSuccessfully"));
        navigate("/profile-company", {
          state: { companyData: responSearchCompany },
        });
      } else {
        toast.error(t("toast.error.failedToSearchCompany"));
      }
    } catch (err) {
      console.error("Error posting answer:", err);
      toast.error(t("toast.error.failedToSearchCompany"));
    }
  };

  return (
    <>
      <main className="mx-auto w-full bg-yellow-50 max-w-[480px]">
        <Helmet>
          <meta name="theme-color" content="#fefce8" />
        </Helmet>
        <LoadingCommon statusLoading={isSearchCompany} />
        <section className="flex justify-center items-center w-full h-[300px]">
          <img src={images.LogoLogin} className="w-[180px]" alt="Logo" />
        </section>
        <section className="flex flex-col justify-between px-4 py-6 w-full bg-white min-h-[calc(100dvh-200px)] rounded-t-[20px] shadow-[0px_0px_4px_rgba(0,0,0,0.08)]">
          <div className="flex flex-col items-center justify-center w-full max-w-[340px] mx-auto">
            <section className="w-full text-center">
              <div className="flex flex-col items-center">
                <h1 className="text-4xl font-bold text-gray-800">
                  {t("companyProfile.title")}
                </h1>
                <div className="mt-3">
                  <span className="inline-block px-3 py-1.5 text-xs text-amber-400 bg-yellow-50 rounded-xl">
                    {t("base.businessAccount")}
                  </span>
                </div>
              </div>
            </section>

            <div className="flex flex-col w-full text-sm text-neutral-500">
              <FormField
                placeholder={t("companyProfile.enterYourWebsite")}
                icon={images.SearchIcon}
                type="text"
                name="searchQuery"
                value={searchQuery}
                onChange={handleInputChange}
                error={errorMessage}
              />
              <button
                onClick={handleSearch}
                className="w-full px-4 py-3 mt-5 text-lg font-semibold text-white bg-[#3F54DF] rounded-xl"
                disabled={isSearchCompany}
              >
                {buttonText}
              </button>
              <div className="mt-5 w-full text-center">
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    sessionStorage.removeItem("accessToken");
                    sessionStorage.removeItem("refreshToken");
                    toast.warning(
                      t("companyProfile.pleaseCompleteCompanyProfile")
                    );
                    navigate("/sign-in");
                  }}
                  className="text-indigo-600 font-bold cursor-pointer"
                >
                  {t("companyProfile.backToSignIn")}
                </span>
              </div>
            </div>
          </div>
          <div className="flex justify-center">
            <Copyright />
          </div>
        </section>
      </main>
    </>
  );
}
